import type { DocNode } from '@atlaskit/adf-schema/doc';

type AdfMark = {
	type?: string;
	attrs?: Record<string, unknown>;
};

type AdfNode = {
	type?: string;
	text?: string;
	attrs?: Record<string, unknown>;
	content?: ReadonlyArray<AdfNode>;
	marks?: AdfMark[];
};

const BLOCK_NODE_TYPES = new Set([
	'paragraph',
	'heading',
	'bulletList',
	'orderedList',
	'blockquote',
	'codeBlock',
]);

const getStringValue = (value: unknown): string => (typeof value === 'string' ? value : '');

const getLinkHref = (marks: AdfMark[] | undefined): string => {
	const linkMark = marks?.find((mark) => mark.type === 'link');
	return getStringValue(linkMark?.attrs?.href);
};

const getExtensionPromptText = (attrs: Record<string, unknown> | undefined): string => {
	const parameters = attrs?.parameters;
	if (!parameters || typeof parameters !== 'object') {
		return '';
	}

	const parameterMap = parameters as Record<string, unknown>;
	const skillName = getStringValue(parameterMap.slug) || getStringValue(parameterMap.name);

	return skillName ? `/${skillName}` : '';
};

const escapeMarkdownLinkText = (text: string): string =>
	text.replace(/\\/g, '\\\\').replace(/]/g, '\\]');

const escapeMarkdownLinkHref = (href: string): string =>
	href.replace(/\\/g, '\\\\').replace(/\)/g, '\\)');

const getTextNodePromptText = (node: AdfNode): string => {
	const text = node.text ?? '';
	const href = getLinkHref(node.marks);

	if (!text && href) {
		return href;
	}

	if (!href || text === href) {
		return text;
	}

	return `[${escapeMarkdownLinkText(text)}](${escapeMarkdownLinkHref(href)})`;
};

const extractFromNode = (node: AdfNode): string => {
	if (node.type === 'text') {
		return getTextNodePromptText(node);
	}

	if (node.type === 'hardBreak') {
		return '\n';
	}

	if (node.type === 'inlineCard' || node.type === 'blockCard' || node.type === 'embedCard') {
		return getStringValue(node.attrs?.url);
	}

	if (node.type === 'mention') {
		return getStringValue(node.attrs?.text);
	}

	if (node.type === 'emoji') {
		return getStringValue(node.attrs?.text) || getStringValue(node.attrs?.shortName);
	}

	if (node.type === 'inlineExtension') {
		return getExtensionPromptText(node.attrs);
	}

	const childText = Array.isArray(node.content) ? node.content.map(extractFromNode).join('') : '';

	if (node.type && BLOCK_NODE_TYPES.has(node.type) && childText) {
		return `${childText}\n`;
	}

	return childText;
};

export const extractPromptTextFromAdf = (adf: DocNode): string =>
	extractFromNode(adf as AdfNode).trim();
